import React, { useEffect, useState } from 'react';
import { api } from '../api';

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayOrders, setTodayOrders] = useState([]);
  const [todayOrdersLoading, setTodayOrdersLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    api.getDashboard()
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));

    api.getOrders({ date_from: today, date_to: today })
      .then(orders => {
        if (!orders || orders.length === 0) return [];
        return Promise.all(orders.map(order => api.getOrder(order.id)));
      })
      .then(details => setTodayOrders(details))
      .catch(() => setTodayOrders([]))
      .finally(() => setTodayOrdersLoading(false));
  }, []);

  const printDailyOrders = () => {
    if (!todayOrders.length) return;
    window.print();
  };

  if (loading) return <div className="loading-center"><span className="spinner" /> Loading dashboard...</div>;
  if (!data) return <div className="page-body"><p className="text-muted">Could not load dashboard. Is the backend running?</p></div>;

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Daily Print Report</h2>
          <div className="text-muted" style={{ marginTop: 6 }}>Print today's full order details: who ordered what, quantity, and amount.</div>
        </div>
        <button
          className="btn btn-primary"
          onClick={printDailyOrders}
          disabled={todayOrdersLoading || todayOrders.length === 0}
        >
          {todayOrdersLoading ? 'Loading...' : 'Print Today'}
        </button>
      </div>

      <div className="stat-grid">
        <div className="stat-card red">
          <div className="stat-label">Today's Revenue</div>
          <div className="stat-value">{fmt(data.today_orders?.t)}</div>
          <div className="stat-sub">{data.today_orders?.c} orders today</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">Month Revenue</div>
          <div className="stat-value" style={{ fontSize: 24 }}>{fmt(data.month_orders?.t)}</div>
          <div className="stat-sub">{data.month_orders?.c} orders this month</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Active Employees</div>
          <div className="stat-value">{data.total_employees?.c}</div>
          <div className="stat-sub">across all departments</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Menu Items</div>
          <div className="stat-value">{data.total_items?.c}</div>
          <div className="stat-sub">available items</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">🧾 Today's Order Details</div>
        {todayOrdersLoading ? (
          <div className="loading-center"><span className="spinner" /> Loading order details...</div>
        ) : todayOrders.length === 0 ? (
          <div className="empty-state"><p>No orders for today to print.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {todayOrders.flatMap(order =>
                  order.items.map(item => (
                    <tr key={`${order.id}-${item.item_id}`}>
                      <td>{order.employee_name}</td>
                      <td style={{ color: 'var(--iocl-muted)' }}>{order.order_date}</td>
                      <td>{item.item_name}</td>
                      <td>{item.quantity}</td>
                      <td className="text-orange">{fmt(item.unit_price)}</td>
                      <td className="text-orange">{fmt(item.subtotal)}</td>
                    </tr>
                  ))
                )}
                <tr className="total-row">
                  <td colSpan={5} style={{ textAlign: 'right' }}>Total today</td>
                  <td>{fmt(todayOrders.reduce((sum, order) => sum + order.total_amount, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="two-col" style={{ gap: 20 }}>
        <div className="card">
          <div className="card-title">🕐 Recent Orders</div>
          {data.recent_orders?.length === 0 ? (
            <div className="empty-state"><p>No orders yet today</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_orders?.map(o => (
                    <tr key={o.id}>
                      <td>{o.employee_name}</td>
                      <td style={{ color: 'var(--iocl-muted)' }}>{o.order_date}</td>
                      <td className="text-orange">{fmt(o.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">🔥 Top Items This Month</div>
          {data.top_items?.length === 0 ? (
            <div className="empty-state"><p>No consumption data yet</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Item</th><th>Qty</th></tr>
                </thead>
                <tbody>
                  {data.top_items?.map((item, i) => (
                    <tr key={item.name}>
                      <td style={{ color: 'var(--iocl-muted)' }}>{i + 1}</td>
                      <td>{item.name}</td>
                      <td><span className="badge badge-orange">{item.qty}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
